import type {
    Line,
    LineId,
    ProcessStep,
    Season,
    Visual,
    Work
} from '@/types'

/*
 * Contenido real y copy final de FMG3D, compartido por las tres propuestas para que
 * la comparación sea solo de diseño. Regla: nada de clientes, reseñas, cifras,
 * precios, plazos ni datos de contacto inventados. Lo que falta se marca con
 * PENDING y se muestra en pantalla como "por confirmar".
 */

export const PENDING = 'por confirmar'

export const studio = {
    name: 'FMG3D',
    email: 'hola@fmg3d.cl',
    // Número de WhatsApp pendiente: cuando exista, el botón directo se activa solo.
    whatsapp: '',
    instagram: '',
    town: 'San Esteban',
    province: 'Los Andes',
    valley: 'Valle del Aconcagua',
    region: 'Región de Valparaíso',
    country: 'Chile',
    printer: 'Bambu Lab P2S',
    material: 'PLA',
    initials: [
        { letter: 'F', name: 'Florencia' },
        { letter: 'M', name: 'Mateo' },
        { letter: 'G', name: 'Gaspar' }
    ],
    motto: 'La impresión 3D es la herramienta. Lo que nos importa es la idea y el objeto que resulta.',
    signoff: 'Una idea a la vez.'
}

export const whatsappHref = studio.whatsapp
    ? `https://wa.me/${studio.whatsapp}?text=${encodeURIComponent('Hola FMG3D, quiero cotizar una pieza.')}`
    : null

/* Voz y copy final */

export const voice = ['Cercana', 'Honesta', 'Cuidada']

export const hero = {
    kicker: 'Taller familiar de diseño y fabricación 3D · San Esteban',
    title: 'Objetos a medida, diseñados y fabricados en familia.',
    lead: 'Macetas, lámparas, piezas para cada temporada y recursos para terapia. Nos cuentas tu idea, con una foto o un dibujo, y la convertimos en un objeto real en nuestro taller de San Esteban.',
    primary: 'Pide tu cotización',
    secondary: 'Ver trabajos'
}

export const heroOptions = [
    {
        label: 'Elegido',
        title: 'Objetos a medida, diseñados y fabricados en familia.',
        why: 'Dice qué hacemos (objetos a medida), cómo (los diseñamos y fabricamos nosotros) y quiénes somos (una familia) en una sola línea. Lo concreto convierte mejor que lo poético, y el “en familia” es algo que un taller industrial no puede decir.'
    },
    {
        label: 'Alternativa',
        title: 'Tu idea, convertida en un objeto que puedes tomar con las manos.',
        why: 'Es más cálida y cercana a “Ideas que toman forma”, pero no dice que es a medida ni quién está detrás. Podría ser el titular de cualquier taller de impresión 3D.'
    }
]

export const sectionCopy = {
    offer: {
        kicker: 'Lo que hacemos',
        title: 'Piezas para la casa, las fechas importantes, la consulta y tu empresa.',
        lead: 'No trabajamos con un catálogo cerrado. Estas son las líneas que más nos piden, y cada una se adapta a lo que necesitas.'
    },
    works: {
        kicker: 'Trabajos del taller',
        title: 'Lo que ya salió del taller.',
        lead: 'Pedidos reales que hemos fabricado. Las fotos de esta versión son referencias: se reemplazan por fotos de cada pieza entregada.'
    },
    idea: {
        kicker: 'Tu idea, nuestro próximo proyecto',
        title: 'No necesitas un archivo 3D. Con una foto o un boceto partimos.',
        lead: 'Tampoco necesitas saber de diseño. Cuéntanos qué quieres, para qué y para cuándo; nosotros resolvemos la forma, el tamaño y la fabricación.'
    },
    process: {
        kicker: 'Cómo trabajamos',
        title: 'Del boceto al objeto, en cuatro pasos.',
        lead: 'Un proceso corto, conversado y sin tecnicismos.'
    },
    seasons: {
        kicker: 'Calendario de temporadas',
        title: 'Cada fecha tiene su pieza, y su fecha límite.',
        lead: 'Las piezas de temporada se fabrican por encargo. Pide con tiempo para llegar a la fecha.'
    },
    about: {
        kicker: 'Quiénes somos',
        title: 'FMG son Florencia, Mateo y Gaspar.',
        paragraphs: [
            'FMG3D es el taller de nuestra familia en San Esteban, en el Valle del Aconcagua. El nombre viene de las iniciales de nuestros hijos.',
            'Partimos pensando en material educativo, por la experiencia docente de la casa. Con el tiempo, lo que más nos pidieron fue otra cosa: una maceta, una lámpara, una pieza para el 18 o una ruleta para una consulta de fonoaudiología.',
            'Hoy trabajamos a pedido y sin apuro de fábrica: cada encargo se conversa, se diseña y se revisa a mano antes de entregarlo.'
        ]
    },
    contact: {
        kicker: 'Contacto',
        title: 'Pide tu cotización.',
        lead: 'Responde cinco preguntas cortas y te contactamos con dudas o un valor de referencia. Si prefieres, escríbenos directo.'
    }
}

export const ideaStarters = [
    'Una foto o un dibujo, aunque sea hecho a mano.',
    'Medidas aproximadas o dónde va a estar la pieza.',
    'Cuántas necesitas.',
    'Para cuándo la necesitas.'
]

export const responseTime = {
    reply: `Respondemos dentro de ${PENDING}`,
    delivery: `Plazo habitual de entrega: ${PENDING}`
}

/* Imágenes: referencias claramente marcadas, nunca presentadas como trabajos reales */

export const sourceLabels = {
    referencia: 'Foto de referencia',
    fmg3d: 'Imagen de fmg3d.cl',
    art: 'Foto pendiente'
} as const

export const visuals = {
    lampara: {
        kind: 'photo',
        src: '/images/ref/lampara.jpg',
        alt: 'Lámpara naranja con forma de sol encendida sobre una repisa clara',
        source: 'referencia'
    },
    maceta: {
        kind: 'photo',
        src: '/images/ref/maceta.jpg',
        alt: 'Maceta blanca con una suculenta sobre una repisa',
        source: 'referencia',
        position: '64% 62%'
    },
    halloween: {
        kind: 'photo',
        src: '/images/ref/halloween.jpg',
        alt: 'Calabazas de Halloween talladas sobre una bandeja blanca',
        source: 'referencia',
        position: '50% 70%'
    },
    navidad: {
        kind: 'photo',
        src: '/images/ref/navidad.jpg',
        alt: 'Adornos navideños pintados a mano sobre un mantel con copos de nieve',
        source: 'referencia'
    },
    manos: {
        kind: 'photo',
        src: '/images/ref/manos.jpg',
        alt: 'Mano que sostiene una pequeña pieza naranja translúcida',
        source: 'referencia',
        position: '50% 35%'
    },
    boceto: {
        kind: 'photo',
        src: '/images/ref/boceto.jpg',
        alt: 'Personas dibujando ideas en un cuaderno sobre una mesa blanca',
        source: 'referencia',
        position: '50% 45%'
    },
    taller: {
        kind: 'photo',
        src: '/images/ref/taller.jpg',
        alt: 'Mesa de trabajo de madera con herramientas bajo luz natural',
        source: 'referencia',
        position: '50% 60%'
    },
    lenguaje: {
        kind: 'photo',
        src: '/images/fmg3d/lenguaje.webp',
        alt: 'Vocales y fichas con apoyos visuales en coral, azul y amarillo',
        source: 'fmg3d'
    },
    ruleta: {
        kind: 'art',
        art: 'ruleta',
        alt: 'Ilustración provisoria de una ruleta de ocho secciones'
    },
    remolino: {
        kind: 'art',
        art: 'remolino',
        alt: 'Ilustración provisoria de un remolino de Fiestas Patrias'
    },
    camion: {
        kind: 'art',
        art: 'camion',
        alt: 'Ilustración provisoria de un camión minero a escala sobre un pedestal'
    },
    pieza: {
        kind: 'art',
        art: 'pieza',
        alt: 'Ilustración provisoria de una pieza impresa en capas'
    }
} satisfies Record<string, Visual>

/* Líneas de oferta, con la URL propuesta para cada una */

export const lines: Line[] = [
    {
        id: 'hogar',
        number: '01',
        title: 'Hogar y regalos',
        short: 'Para la casa y para regalar',
        description: 'Macetas, lámparas temáticas y objetos decorativos, pensados para un lugar o una persona en particular.',
        examples: 'Macetas, lámparas, objetos decorativos',
        url: '/hogar-y-regalos',
        visual: visuals.lampara
    },
    {
        id: 'celebraciones',
        number: '02',
        title: 'Celebraciones y temporadas',
        short: 'Para las fechas que importan',
        description: 'Piezas para Fiestas Patrias, Halloween y Navidad. Cada temporada tiene fecha límite de pedido.',
        examples: 'Fiestas Patrias, Halloween, Navidad',
        url: '/celebraciones',
        visual: visuals.halloween
    },
    {
        id: 'terapia',
        number: '03',
        title: 'Terapia y aprendizaje',
        short: 'Para la consulta y la sala',
        description: 'Ruletas para fonoaudiología y recursos para trabajar con las manos, ajustados a cada profesional.',
        examples: 'Ruletas, recursos manipulativos',
        url: '/terapia-y-aprendizaje',
        visual: visuals.ruleta
    },
    {
        id: 'a-medida',
        number: '04',
        title: 'Pedidos a medida',
        short: 'Para lo que todavía no existe',
        description: '¿No está en la lista? Con una foto o un boceto partimos. No necesitas un archivo 3D.',
        examples: 'Piezas únicas, regalos especiales',
        url: '/a-medida',
        visual: visuals.manos
    },
    {
        id: 'mineria',
        number: '05',
        title: 'Minería y regalos corporativos',
        short: 'Una línea en exploración',
        description: 'Camiones a escala y recuerdos para eventos. Nos lo han pedido y queremos desarrollarlo bien: conversemos tu caso.',
        examples: 'Modelos a escala, recuerdos de eventos',
        url: '/mineria-y-empresas',
        status: 'En exploración',
        visual: visuals.camion
    }
]

export const lineTitles: Record<LineId, string> = {
    hogar: 'Hogar y regalos',
    celebraciones: 'Celebraciones',
    terapia: 'Terapia y aprendizaje',
    'a-medida': 'A medida',
    mineria: 'Minería y empresas'
}

/* Trabajos reales, en formato fijo: necesidad, solución y resultado */

export const works: Work[] = [
    {
        id: 'ruletas',
        number: '01',
        title: 'Ruletas para fonoaudiólogas',
        line: 'terapia',
        type: 'Recurso terapéutico',
        description: 'Ruletas giratorias para sesiones de fonoaudiología, con el contenido que cada profesional necesita.',
        need: 'Un apoyo visual y giratorio para trabajar en sesión.',
        solution: 'Ruleta personalizada, diseñada a la medida de la consulta e impresa en PLA.',
        result: null,
        visual: visuals.ruleta
    },
    {
        id: 'lamparas',
        number: '02',
        title: 'Lámparas temáticas',
        line: 'hogar',
        type: 'Decoración',
        description: 'Lámparas con un motivo elegido, para un dormitorio, un rincón o un regalo.',
        need: 'Una lámpara con un tema que no se encuentra en tiendas.',
        solution: 'Diseño del motivo y fabricación de la pantalla a pedido.',
        result: null,
        visual: visuals.lampara
    },
    {
        id: 'macetas',
        number: '03',
        title: 'Macetas decorativas',
        line: 'hogar',
        type: 'Decoración',
        description: 'Macetas para interior con formas que no se encuentran en una tienda.',
        need: 'Macetas con una forma o un color específico.',
        solution: 'Modelo adaptado al tamaño de la planta y al espacio.',
        result: null,
        visual: visuals.maceta
    },
    {
        id: 'halloween',
        number: '04',
        title: 'Artículos de Halloween',
        line: 'celebraciones',
        type: 'Temporada · octubre',
        description: 'Decoración y piezas temáticas para que octubre se note en la casa.',
        need: 'Decoración temática distinta a la de siempre.',
        solution: 'Piezas de temporada fabricadas a pedido.',
        result: null,
        visual: visuals.halloween
    },
    {
        id: 'navidad',
        number: '05',
        title: 'Productos navideños',
        line: 'celebraciones',
        type: 'Temporada · diciembre',
        description: 'Adornos y detalles para decorar y regalar en diciembre.',
        need: 'Adornos y regalos con un toque propio.',
        solution: 'Piezas navideñas diseñadas y fabricadas en el taller.',
        result: null,
        visual: visuals.navidad
    },
    {
        id: 'fiestas-patrias',
        number: '06',
        title: 'Artículos para Fiestas Patrias',
        line: 'celebraciones',
        type: 'Temporada · septiembre',
        description: 'Piezas para celebrar el 18 en la casa, la oficina o el colegio.',
        need: 'Decoración dieciochera para la casa o la oficina.',
        solution: 'Piezas de temporada hechas a pedido.',
        result: null,
        visual: visuals.remolino
    },
    {
        id: 'encargos',
        number: '07',
        title: 'Encargos especiales',
        line: 'a-medida',
        type: 'A medida',
        description: 'Piezas únicas que nacen de lo que nos cuenta cada persona.',
        need: 'Algo que no existe en ninguna tienda.',
        solution: 'Partimos de una foto o un boceto, diseñamos y fabricamos.',
        result: null,
        visual: visuals.manos
    }
]

/* Del boceto al objeto */

export const processSteps: ProcessStep[] = [
    {
        number: '01',
        stage: 'Idea',
        title: 'Conversamos',
        text: 'Por WhatsApp o correo entendemos qué necesitas, para quién es y para cuándo.'
    },
    {
        number: '02',
        stage: 'Modelo',
        title: 'Diseñamos o adaptamos',
        text: 'Modelamos la pieza o adaptamos una base existente. Te contamos cómo va a quedar antes de imprimir.'
    },
    {
        number: '03',
        stage: 'Impresión',
        title: 'Fabricamos',
        text: 'Imprimimos en PLA con una Bambu Lab P2S y revisamos cada pieza a mano.'
    },
    {
        number: '04',
        stage: 'Pieza',
        title: 'Entregamos',
        text: 'Coordinamos contigo cómo y cuándo recibirla.'
    }
]

export const materialGuide = {
    title: 'PLA: lo que conviene saber',
    items: [
        'Es un plástico de origen vegetal (almidón de maíz o caña), liviano y firme.',
        'Se ablanda con el calor: sobre unos 55 °C puede deformarse. No lo dejes dentro del auto al sol ni lo laves con agua caliente.',
        'Para limpiarlo basta un paño húmedo.',
        'Si la pieza va al exterior o cerca de una fuente de calor, cuéntanos antes para evaluarlo.'
    ]
}

export const seasons: Season[] = [
    {
        month: 'Diciembre',
        short: 'Dic',
        title: 'Navidad',
        text: 'Adornos y regalos hechos a pedido.',
        next: true,
        visual: visuals.navidad
    },
    {
        month: 'Septiembre',
        short: 'Sep',
        title: 'Fiestas Patrias',
        text: 'Decoración y detalles para el 18.',
        visual: visuals.remolino
    },
    {
        month: 'Octubre',
        short: 'Oct',
        title: 'Halloween',
        text: 'Piezas temáticas para decorar y regalar.',
        visual: visuals.halloween
    }
]

/* Cotizador guiado */

export const quoteSteps = {
    need: {
        question: '¿Qué necesitas?',
        options: ['Algo para la casa o un regalo', 'Una pieza de temporada', 'Un recurso para terapia o aprendizaje', 'Algo para mi empresa o un evento', 'Otra cosa']
    },
    size: {
        question: '¿De qué tamaño, más o menos?',
        options: ['Cabe en la mano', 'Como un libro', 'Más grande', 'No lo sé todavía']
    },
    quantity: {
        question: '¿Cuántas piezas?',
        options: ['1', 'De 2 a 10', 'De 11 a 50', 'Más de 50']
    },
    date: {
        question: '¿Para cuándo la necesitas?'
    },
    contact: {
        question: '¿Cómo te contactamos?'
    }
}

export const needByLine: Record<LineId, string> = {
    hogar: 'Algo para la casa o un regalo',
    celebraciones: 'Una pieza de temporada',
    terapia: 'Un recurso para terapia o aprendizaje',
    'a-medida': 'Otra cosa',
    mineria: 'Algo para mi empresa o un evento'
}

/** Anclas de la home, iguales en las tres propuestas. */
export const sections = [
    { id: 'lo-que-hacemos', title: 'Lo que hacemos' },
    { id: 'trabajos', title: 'Trabajos del taller' },
    { id: 'tu-idea', title: 'Tu idea' },
    { id: 'proceso', title: 'Cómo trabajamos' },
    { id: 'nosotros', title: 'Quiénes somos' },
    { id: 'contacto', title: 'Pide tu cotización' }
]
